import { createLogger, format, transports } from 'winston'
import { PostHogTransport } from './winston-posthog'
import { fullFormat } from 'winston-error-format'
import stringify from 'json-stringify-safe'
import {IS_DEV, LOG_LEVEL} from "@/constants";
import {GameGlassUser} from "@/hooks/useCurrentUser";

// Polyfill because winston uses an old API
// @ts-ignore we know it's not there...
if (!window.setImmediate) {
	// @ts-ignore for some reason, it's trying to use node's setImmediate function type...
	window.setImmediate = (callback) => {
		setTimeout(callback, 0)
	}
}

// Strips underscore-prefixed keys. `depth + 1` (not `depth++`) matters: the
// post-increment passed the same depth down every branch, so the guard never
// bit and a cyclic object recursed until the stack blew.
const stripPrivate = (data: any, depth = 0): any => {
	Object.keys(data).forEach((key) => {
		if (key.startsWith('_')) {
			delete data[key]
		} else if (depth < 5 && data[key] && typeof data[key] === 'object') {
			data[key] = stripPrivate(data[key], depth + 1)
		}
	})
	return data
}

// Clerk bearer tokens ride on axios config/response headers. They must never
// reach PostHog — and splat args now get serialized where they were previously
// dropped, so redaction has to happen before anything is stringified.
const SECRET_HEADERS = ['authorization', 'cookie', 'set-cookie', 'x-api-key']

const redactHeaders = (headers: any) => {
	if (!headers || typeof headers !== 'object') return headers
	Object.keys(headers).forEach((key) => {
		if (SECRET_HEADERS.includes(key.toLowerCase())) {
			headers[key] = '[redacted]'
		}
	})
	return headers
}

const stripAxios = (data: any) => {
	if (data?.config?.headers) redactHeaders(data.config.headers)
	if (data?.headers) redactHeaders(data.headers)

	if (data.name === 'AxiosError') {
		return {
			name: data.name,
			code: data.code,
			config: data.config
		}
	}
	if (data?.config && data?.headers && data?.request) {
		delete data.request
		// Axios response object
		if (data.config.headers?.['content-length'] > 10000) {
			data.config.data = 'Data too large'
		}

		if (data.headers?.['content-length'] > 10000) {
			data.data = 'Data too large'
		}
	}
	return data
}

const privateFormatter = format((info) => stripPrivate(info))

const axiosFormatter = format((info) => {
	;['response', 'result', 'error'].forEach((key) => {
		if (info?.[key]) {
			info[key] = stripAxios(info[key])
		}
	})
	return stripAxios(info)
})

// Winston puts every console argument after the first onto this symbol. Nothing
// in the format chain read it, so `console.error('[PostHog.js]', 'real message')`
// was recorded as just "[PostHog.js]" and the message was lost. `format.splat()`
// is not the fix — with a string arg and no %-token it does
// `Object.assign(info, 'some message')`, spraying character indices into the record.
const SPLAT = Symbol.for('splat')
const FORMAT_TOKEN = /%[scdjifoO%]/

// Winston folds the *first* argument into `info` itself when it is an object and
// the message carries no %-token (winston-error-format does the same for Errors),
// so that one is already covered by `stringifiedRest` and rendering it again
// would print it twice. Everything else — every string, and any object that is
// not in first position — is dropped unless we render it here.
const splatToRender = (info: any): unknown[] => {
	const splat = info[SPLAT] as unknown[] | undefined
	if (!splat?.length) return []
	const firstWasMerged =
		typeof splat[0] === 'object' &&
		splat[0] !== null &&
		!FORMAT_TOKEN.test(String(info.message))
	return firstWasMerged ? splat.slice(1) : splat
}

const formatSplatArg = (arg: unknown): string => {
	if (typeof arg === 'string') return arg
	if (arg instanceof Error) return arg.stack || `${arg.name}: ${arg.message}`
	if (arg && typeof arg === 'object') {
		try {
			// Same scrubbing the top-level info gets, so a bearer token on an
			// axios error cannot reach PostHog just by being a second argument.
			return stringify(stripPrivate(stripAxios(arg)))
		} catch (_err) {
			return '[unserializable]'
		}
	}
	return String(arg)
}

const posthogTransport = new PostHogTransport('frontend')
export const logger = createLogger({
	level: LOG_LEVEL || 'info',
	format: format.combine(
		fullFormat(),
		privateFormatter(),
		axiosFormatter(),
		format.timestamp({
			format: 'YYYY-MM-DD HH:mm:ss'
		}),
		format.printf((info) => {
			try {
				const extras = splatToRender(info)
				const message = extras.length
					? `${info.message} ${extras.map(formatSplatArg).join(' ')}`
					: `${info.message}`

				// Symbol keys are not copied by spread, so SPLAT cannot double-print here.
				const stringifiedRest = stringify({
					...info,
					level: undefined,
					message: undefined,
					splat: undefined,
					timestamp: undefined
				})
				if (stringifiedRest !== '{}') {
					return `[${(info as any).timestamp}][${info.level}]: ${message} ${stringifiedRest}`
				}
				return `[${(info as any).timestamp}][${info.level}]: ${message}`
			} catch (_err) {
				// Do nothing
			}
			return ''
		})
	),
	handleExceptions: true,
	handleRejections: true,
	exitOnError: false,
	transports: [posthogTransport],
	defaultMeta: {
		version: import.meta.env.PACKAGE_VERSION
	}
})

if (IS_DEV) {
	logger.add(new transports.Console())
}

console.error = logger.error = logger.error.bind(logger)
console.warn = logger.warn = logger.warn.bind(logger)
console.info = logger.info = logger.info.bind(logger)
console.debug = logger.debug = logger.debug.bind(logger)
console.log = logger.debug = logger.debug.bind(logger)

export const setLoggerUser = (user?: Partial<GameGlassUser>) => {
	posthogTransport.setUserId(user?.id)
}
